import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { HiLockClosed } from "react-icons/hi";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { EmptyState } from "../../../atoms/EmptyState";
import { Spinner } from "../../../atoms/Spinner";
import ConfirmModal from "../../../public/ConfirmModal";
import NoTranslate from "../../../atoms/NoTranslate";
import AssigneePicker, { PickedUser } from "../todos/AssigneePicker";
import { useResolveAssignees } from "../../../../hooks/todos/useResolveAssignees";
import useAuth from "../../../../hooks/useAuth";
import { agendaRights } from "../../../../hooks/agenda/useAgendaAccess";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import ProfileLink, { profilePath } from "./ProfileLink";
import { AppointmentPaymentBar } from "./AppointmentPayment";
import { awaitsPayment, paymentPrice, paymentViewOf } from "./agendaPayment";
import { getLanguageCode } from "../../../../utils/languageUtility";
import {
  Appointment,
  AppointmentInput,
  AppointmentSummary,
  useCreateAppointment,
  useDeleteAppointment,
  forgetAppointment,
  refreshAppointmentLists,
  useGetAppointment,
  useUpdateAppointment,
} from "../../../../hooks/agenda";
import {
  DEFAULT_DURATION_MINUTES,
  browserTimeZone,
  fromLocalInput,
  nextFullHour,
  toLocalInput,
} from "./agendaDates";
import {
  APPOINTMENT_LIMITS,
  AppointmentDraft,
  appointmentActions,
  appointmentPatch,
  isTooLong,
  locatingParticipant,
  lockedBookingPerson,
  participantsProblem,
  withoutDeleted,
} from "./appointmentDraft";
import { useAgendaFailure } from "./agendaTypes";

export interface AppointmentModalProps {
  show: boolean;
  onClose: () => void;
  appointment?: AppointmentSummary | null;
  /** The appointment already carries its whole record (a person's card): nothing to load. */
  detailed?: boolean;
  /** Prefills a new appointment, read once per opening. */
  draft?: AppointmentDraft;
  /** Fewest participants of a new appointment: 2 from another person's card. */
  minParticipants?: number;
  /** The person whose agenda or card the appointment was opened from. */
  openedFrom?: string | null;
  /** The appointment no longer exists: deleted, or released once its payment time ran out. */
  onGone?: (id: string) => void;
  /** The appointment as the server changed it: it may have moved to another day. */
  onChanged?: (appointment: Appointment) => void;
}

interface FormValues {
  title: string;
  start: string;
  end: string;
  location: string;
  description: string;
}

const NO_GROUPS: string[] = [];
const ignoreGroups = () => undefined;

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  show,
  onClose,
  appointment,
  detailed = false,
  draft,
  minParticipants = 1,
  openedFrom,
  onGone,
  onChanged,
}) => {
  const { t, i18n } = useTranslation();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;
  const permissions = usePermissions(user);
  const isEdit = Boolean(appointment);
  const locatedAt = appointment
    ? locatingParticipant(appointment, openedFrom)
    : undefined;

  const rights = agendaRights(permissions);
  const canOpenProfiles = permissions.can(Cap.USERS_DETAILS, Level.VIEW);
  const { managesOthers } = rights;

  const createAppointment = useCreateAppointment(customerId, entityId);
  const updateAppointment = useUpdateAppointment(customerId, entityId);
  const deleteAppointment = useDeleteAppointment(customerId, entityId);
  const isSaving = createAppointment.isLoading || updateAppointment.isLoading;
  const isWriting = isSaving || deleteAppointment.isLoading;
  const listedWhole = detailed && !(appointment && awaitsPayment(appointment));
  const detailQuery = useGetAppointment(
    customerId,
    entityId,
    appointment?.id ?? "",
    appointment?.day ?? "",
    locatedAt ?? "",
    // A write drops the detail, and a move answers 404 at the former day: no reading meanwhile.
    { enabled: show && isEdit && !listedWhole && !isWriting }
  );
  const detail: Appointment | null = !appointment
    ? null
    : listedWhole
    ? (appointment as Appointment)
    : detailQuery.data ?? null;
  const { canModify, canDelete } = appointmentActions(
    rights,
    detail ?? appointment,
    user.user_identification
  );
  const lockedPerson = canModify ? lockedBookingPerson(detail) : null;
  const detailFresh =
    Boolean(detail) && (listedWhole || !detailQuery.isFetching);
  const gone =
    detailQuery.isError && detailQuery.error?.response?.status === 404;

  const readDetail = detailQuery.isFetching ? null : detailQuery.data;
  const changedSinceListed =
    Boolean(appointment && readDetail) &&
    (readDetail?.status !== appointment?.status ||
      readDetail?.payment?.status !== appointment?.payment?.status);
  useEffect(() => {
    if (changedSinceListed) refreshAppointmentLists(entityId);
  }, [changedSinceListed, entityId]);

  const goneId = gone ? appointment?.id : undefined;
  const forgotten = useRef<string | null>(null);
  useEffect(() => {
    if (!goneId || forgotten.current === goneId) return;
    forgotten.current = goneId;
    forgetAppointment(entityId);
    onGone?.(goneId);
    // Once per appointment found gone; onGone may change identity at each render of the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goneId, entityId]);

  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [participantsError, setParticipantsError] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: "",
      start: "",
      end: "",
      location: "",
      description: "",
    },
  });

  const [filledFor, setFilledFor] = useState<string | null>(null);
  const formKey = appointment?.id ?? "";
  const detailPending =
    isEdit && (filledFor !== formKey || (!detail && !isSaving));

  useEffect(() => {
    if (!show) {
      setFilledFor(null);
      return;
    }
    // Filled once per opening, from a fresh read: a refetch on window focus must not wipe what is being typed.
    if ((isEdit && !detailFresh) || filledFor === formKey) return;
    const prefill = detail ? undefined : draft;
    const start = detail?.start ?? prefill?.start ?? nextFullHour();
    const end =
      detail?.end ??
      prefill?.end ??
      moment(start).add(DEFAULT_DURATION_MINUTES, "minute").valueOf();
    reset({
      title: detail?.title ?? prefill?.title ?? "",
      start: toLocalInput(start),
      end: toLocalInput(end),
      location: detail?.location ?? prefill?.location ?? "",
      description: detail?.description ?? prefill?.description ?? "",
    });
    setParticipantIds(
      detail
        ? detail.participants.map((p) => p.user_identification)
        : Array.from(new Set(prefill?.participantIds ?? []))
    );
    setConfirmDelete(false);
    setParticipantsError("");
    setFilledFor(formKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, formKey, detailFresh]);

  const { resolvedUsers: fetchedUsers, isResolving } = useResolveAssignees(
    customerId,
    entityId,
    show ? participantIds : [],
    NO_GROUPS
  );

  const resolvedUsers = useMemo(() => {
    const fromAppointment: Record<string, PickedUser> = {};
    appointment?.participants.forEach((p) => {
      fromAppointment[p.user_identification] = {
        id: p.user_identification,
        name: p.name ?? "",
      };
    });
    const merged: Record<string, PickedUser> = { ...fromAppointment };
    Object.entries(fetchedUsers).forEach(([id, u]) => {
      merged[id] = {
        ...fromAppointment[id],
        ...u,
        name: u.name || merged[id]?.name || "",
      };
    });
    return merged;
  }, [appointment, fetchedUsers]);

  const organizerName = useMemo(() => {
    if (!appointment) return "";
    return (
      appointment.participants.find(
        (p) => p.user_identification === appointment.organizer
      )?.name ?? ""
    );
  }, [appointment]);

  const appointmentTimeZone = detail?.timezone;
  const showTimeZoneHint =
    Boolean(appointmentTimeZone) && appointmentTimeZone !== browserTimeZone();

  const participantsMessage = (ids: string[]): string => {
    const problem = participantsProblem({
      ids,
      before: appointment?.participants.map((p) => p.user_identification),
      author: { id: user.user_identification, managesOthers },
      minParticipants,
    });
    return problem
      ? (t(`agenda.modal.${problem}`, {
          max: APPOINTMENT_LIMITS.participants,
        }) as string)
      : "";
  };

  const onSubmit = (values: FormValues) => {
    if (!canModify || detailPending) return;
    const start = fromLocalInput(values.start);
    const end = fromLocalInput(values.end);
    if (start === null || end === null) return;
    const participants = withoutDeleted(participantIds, appointment);
    const problem = participantsMessage(participants);
    if (problem) {
      setParticipantsError(problem);
      return;
    }
    const input: AppointmentInput = {
      start,
      end,
      timezone: browserTimeZone(),
      title: values.title.trim(),
      location: values.location.trim(),
      description: values.description.trim(),
      participants,
      lang: getLanguageCode(i18n.language),
    };
    if (appointment) {
      if (!detail) return;
      const patch = appointmentPatch(detail, input);
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      updateAppointment.mutate(
        { id: appointment.id, participant: locatedAt, patch },
        {
          onSuccess: (updated) => {
            toast.success(t("agenda.modal.updated") as string);
            if (updated) onChanged?.(updated);
            onClose();
          },
          onError: (err) => {
            toast.error(failure(err, "agenda.modal.saveError"));
          },
        }
      );
      return;
    }
    createAppointment.mutate(input, {
      onSuccess: () => {
        toast.success(t("agenda.modal.created") as string);
        onClose();
      },
      onError: (err) => {
        toast.error(failure(err, "agenda.modal.saveError"));
      },
    });
  };

  const onDelete = () => {
    if (!appointment) return;
    deleteAppointment.mutate(
      { id: appointment.id, participant: locatedAt },
      {
        onSuccess: () => {
          toast.success(t("agenda.modal.deleted") as string);
          setConfirmDelete(false);
          onGone?.(appointment.id);
          onClose();
        },
        onError: (err) => {
          setConfirmDelete(false);
          toast.error(failure(err, "agenda.modal.deleteError"));
        },
      }
    );
  };

  const onStartChange = () => {
    const start = fromLocalInput(getValues("start"));
    const end = fromLocalInput(getValues("end"));
    if (start !== null && (end === null || end <= start)) {
      setValue(
        "end",
        toLocalInput(
          moment(start).add(DEFAULT_DURATION_MINUTES, "minute").valueOf()
        )
      );
    }
  };

  const lengthCounter = (field: "title" | "location" | "description") =>
    canModify ? (
      <CharacterCounter
        currentLength={(watch(field) || "").length}
        maxLength={APPOINTMENT_LIMITS[field]}
        className="mt-1"
      />
    ) : null;

  const payment = detail?.payment ?? null;
  const payer = payment?.payer;
  const payerName = payer
    ? resolvedUsers[payer]?.name ||
      appointment?.participants.find((p) => p.user_identification === payer)
        ?.name ||
      ""
    : "";
  const paidPrice = paymentPrice(payment, i18n.language);
  const paymentView = detail ? paymentViewOf(detail) : null;
  const deleteDescription =
    paymentView === null ? (
      t("agenda.modal.deleteDescription")
    ) : (
      <>
        <p>{t("agenda.modal.deleteDescription")}</p>
        <p className="mt-2">
          {payment?.status !== "paid"
            ? t("agenda.modal.deleteUnpaid")
            : paidPrice
            ? t("agenda.modal.deletePaid", { price: paidPrice })
            : t("agenda.modal.deletePaidNoAmount")}
        </p>
        {payment?.status === "paid" &&
          canOpenProfiles &&
          payer &&
          payerName && (
            <p className="mt-2">
              <ProfileLink href={profilePath(payer)} name={payerName} />
            </p>
          )}
      </>
    );

  const title = !isEdit
    ? t("agenda.modal.createTitle")
    : canModify
    ? t("agenda.modal.editTitle")
    : t("agenda.modal.viewTitle");

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="2xl"
        dismissible
        title={title}
        subtitle={
          appointment ? (
            <>
              {moment(appointment.start).format("LLLL")}
              {organizerName && (
                <>
                  {" · "}
                  {t("agenda.modal.organizedBy")}{" "}
                  <NoTranslate>{organizerName}</NoTranslate>
                </>
              )}
            </>
          ) : undefined
        }
        onSubmit={handleSubmit(onSubmit)}
        footer={
          gone ? (
            <CustomButton
              type="button"
              variant="secondary"
              title={t("common.close") as string}
              onClick={onClose}
            />
          ) : (
            <>
              {canDelete && (
                <CustomButton
                  type="button"
                  variant="secondary"
                  icon="trash"
                  className="mr-auto text-red-600"
                  title={t("common.delete") as string}
                  onClick={() => setConfirmDelete(true)}
                  disabled={isSaving}
                />
              )}
              <CustomButton
                type="button"
                variant="secondary"
                title={
                  (canModify ? t("common.cancel") : t("common.close")) as string
                }
                onClick={onClose}
                disabled={isSaving}
              />
              {canModify && (
                <CustomButton
                  type="submit"
                  variant="primary"
                  title={
                    (isEdit
                      ? t("agenda.modal.save")
                      : t("agenda.modal.create")) as string
                  }
                  isLoading={isSaving}
                  disabled={isSaving || detailPending}
                />
              )}
            </>
          )
        }
      >
        {gone ? (
          <EmptyState title={t("agenda.modal.gone")} />
        ) : detailPending ? (
          detailQuery.isError ? (
            <EmptyState
              title={t("agenda.modal.loadError")}
              action={
                <CustomButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  title={t("agenda.retry") as string}
                  onClick={() => detailQuery.refetch()}
                />
              }
            />
          ) : (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          )
        ) : (
          <div className="space-y-4">
            {detail && (
              <AppointmentPaymentBar
                appointment={detail}
                participant={locatedAt}
                canCreateLink={canModify}
              />
            )}
            <div>
              <CustomInput
                name="title"
                control={control}
                label={t("agenda.modal.titleLabel") as string}
                placeholder={t("agenda.modal.titlePlaceholder") as string}
                required
                disabled={!canModify}
                maxLength={APPOINTMENT_LIMITS.title}
                rules={{
                  validate: (value: string) =>
                    value.trim().length > 0 ||
                    (t("agenda.modal.titleRequired") as string),
                }}
                error={errors.title}
                helperText={errors.title}
              />
              {lengthCounter("title")}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CustomInput
                name="start"
                type="datetime-local"
                control={control}
                label={t("agenda.modal.startLabel") as string}
                required
                disabled={!canModify}
                step={900}
                onValueChange={onStartChange}
                rules={{
                  validate: (value: string) =>
                    fromLocalInput(value) !== null ||
                    (t("agenda.modal.dateRequired") as string),
                }}
                error={errors.start}
                helperText={errors.start}
              />
              <CustomInput
                name="end"
                type="datetime-local"
                control={control}
                label={t("agenda.modal.endLabel") as string}
                required
                disabled={!canModify}
                step={900}
                rules={{
                  validate: (value: string) => {
                    const end = fromLocalInput(value);
                    const start = fromLocalInput(getValues("start"));
                    if (end === null)
                      return t("agenda.modal.dateRequired") as string;
                    if (start !== null && end <= start)
                      return t("agenda.modal.endBeforeStart") as string;
                    if (start !== null && isTooLong(start, end))
                      return t("agenda.refusals.TOO_LONG") as string;
                    return true;
                  },
                }}
                error={errors.end}
                helperText={errors.end}
              />
            </div>
            {showTimeZoneHint && (
              <p className="-mt-2 text-xs text-gray-500">
                {t("agenda.modal.timeZoneHint", {
                  timezone: browserTimeZone(),
                })}
              </p>
            )}

            <div>
              <CustomInput
                name="location"
                control={control}
                label={t("agenda.modal.locationLabel") as string}
                placeholder={t("agenda.modal.locationPlaceholder") as string}
                disabled={!canModify}
                maxLength={APPOINTMENT_LIMITS.location}
              />
              {lengthCounter("location")}
            </div>

            <div>
              <CustomInput
                name="description"
                control={control}
                label={t("agenda.modal.descriptionLabel") as string}
                disabled={!canModify}
                rows={3}
                maxLength={APPOINTMENT_LIMITS.description}
              />
              {lengthCounter("description")}
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">
                {t("agenda.modal.participantsLabel")}
                {participantIds.length > 0 && (
                  <span className="ml-1 tabular-nums text-gray-400">
                    ({participantIds.length})
                  </span>
                )}
              </p>
              <AssigneePicker
                customerId={customerId}
                entityId={entityId}
                userIds={participantIds}
                groupIds={NO_GROUPS}
                resolvedUsers={resolvedUsers}
                resolvedGroups={{}}
                onChangeUsers={(ids) => {
                  setParticipantIds(ids);
                  setParticipantsError("");
                }}
                onChangeGroups={ignoreGroups}
                allowGroups={false}
                splitByRole={false}
                resolving={isResolving}
                searchPlaceholder={
                  t("agenda.modal.participantsSearch") as string
                }
                emptyText={t("agenda.modal.participantsEmpty") as string}
                disabled={!canModify}
                lockedUserId={lockedPerson}
                userAction={(id, person) => (
                  <>
                    {canOpenProfiles && person?.name && (
                      <ProfileLink
                        href={profilePath(id)}
                        name={person.name}
                        compactOnPhone
                      />
                    )}
                    {id === lockedPerson && (
                      <HiLockClosed
                        size={16}
                        aria-hidden="true"
                        className="mx-1.5 shrink-0 text-gray-400"
                      />
                    )}
                  </>
                )}
              />
              {lockedPerson && (
                <p className="mt-2 text-xs text-gray-500">
                  {t("agenda.refusals.BOOKING_PERSON_LOCKED")}
                </p>
              )}
              {participantsError && (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {participantsError}
                </p>
              )}
            </div>
          </div>
        )}
      </ModalShell>

      <ConfirmModal
        show={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("agenda.modal.deleteTitle")}
        description={deleteDescription}
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={() => setConfirmDelete(false)}
        confirmBtnName={t("common.delete")}
        confirmBtnFn={onDelete}
        ConfirmBtnColor="red"
        isLoading={deleteAppointment.isLoading}
      />
    </>
  );
};

export default AppointmentModal;
